import type { AuthUser } from '@modules/auth/types'
import { normalizeNextPath, targetForPath } from './nextPath'

export type SurfaceId = 'admin' | 'vakt'

export type Surface = {
  id: SurfaceId
  title: string
  description: string
  href: string
}

export type PortalDecision =
  | { kind: 'login' }
  | { kind: 'redirect'; href: string }
  | { kind: 'choose'; surfaces: Surface[] }
  | { kind: 'no_access'; attempted: SurfaceId | null }

const ADMIN_PERMISSIONS = new Set(['can_access_admin'])

const VAKT_PERMISSIONS = new Set([
  'can_access_vakt',
  'vakt',
  'vakt:access',
  'vakt:*',
  'portal:vakt',
])

const ADMIN_SURFACE: Surface = {
  id: 'admin',
  title: 'Admin',
  description: 'Kundeoppsett, rapporter, brukere og drift.',
  href: '/admin/',
}

const VAKT_SURFACE: Surface = {
  id: 'vakt',
  title: 'Vakt',
  description: 'Vaktflate, QR-scan og sjekklister.',
  href: '/vakt/',
}

function hasPermission(user: AuthUser, allowed: Set<string>): boolean {
  return user.permissions.some((permission) => allowed.has(permission.toLowerCase()))
}

export function canOpenAdmin(user: AuthUser): boolean {
  return user.role !== 'staff' && hasPermission(user, ADMIN_PERMISSIONS)
}

export function canOpenVakt(user: AuthUser): boolean {
  return hasPermission(user, VAKT_PERMISSIONS)
}

export function getAvailableSurfaces(user: AuthUser): Surface[] {
  const surfaces: Surface[] = []
  if (canOpenAdmin(user)) surfaces.push(ADMIN_SURFACE)
  if (canOpenVakt(user)) surfaces.push(VAKT_SURFACE)
  return surfaces
}

export function resolvePortalDecision(user: AuthUser | null, next?: string): PortalDecision {
  if (!user) return { kind: 'login' }

  const normalizedNext = normalizeNextPath(next)
  const attempted = targetForPath(normalizedNext)
  const surfaces = getAvailableSurfaces(user)

  if (normalizedNext && attempted && surfaces.some((surface) => surface.id === attempted)) {
    return { kind: 'redirect', href: normalizedNext }
  }

  if (normalizedNext && attempted) {
    return { kind: 'no_access', attempted }
  }

  if (surfaces.length === 1) {
    return { kind: 'redirect', href: surfaces[0].href }
  }

  if (surfaces.length > 1) {
    return { kind: 'choose', surfaces }
  }

  return { kind: 'no_access', attempted: null }
}
